import { useCallback, useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { notify } from '@/components/shared';
import { api, isApiError, queryKeys, STALE_TIME, type SoftphoneTarget } from '@/lib/api';
import { useEntitlements } from '@/lib/entitlements';
import {
  ESTADO_INICIAL,
  esTecla,
  estaOcupado,
  motivoDeMicrofono,
  resultadoDe,
  segundosDeConversacion,
  transicion,
  type DestinoDeLlamada,
  type EventoDeLlamada,
} from './estado';
import { abrirMicrofono, cerrarMicrofono } from './microfono';
import type { LlamadaSip } from './sesion-sip';
import { SoftphoneContext, type Softphone } from './use-softphone';

const OCUPADO = 'Ya tienes una llamada en curso. Cuélgala antes de llamar a otra persona.';
const SIN_INICIAR = 'No pudimos iniciar la llamada.';

const objetivo = (destino: DestinoDeLlamada): SoftphoneTarget | null => {
  if (destino.casoId && !destino.contactId) return { casoId: destino.casoId };
  if (destino.contactId && !destino.casoId) return { contactId: destino.contactId };
  return null;
};

export function SoftphoneProvider({ children }: { children: ReactNode }) {
  const { tenant, hasModule } = useEntitlements();
  const conModulo = hasModule('calls') || hasModule('escalation');
  const config = useQuery({
    queryKey: queryKeys.softphone(tenant.id),
    queryFn: () => api.softphone.config(tenant.id),
    staleTime: STALE_TIME.reference,
    enabled: conModulo,
    retry: false,
  });
  const enabled = conModulo && config.data?.enabled === true;
  const configLista = !config.isLoading;

  const [state, despachar] = useReducer(transicion, ESTADO_INICIAL);
  const estadoRef = useRef(state);
  estadoRef.current = state;
  const llamadaRef = useRef<LlamadaSip | null>(null);
  const intentoRef = useRef(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [ahora, setAhora] = useState(() => Date.now());
  const [reclamos, setReclamos] = useState(0);

  useEffect(() => {
    if (state.fase !== 'in_call') return;
    setAhora(Date.now());
    const reloj = window.setInterval(() => setAhora(Date.now()), 1000);
    return () => window.clearInterval(reloj);
  }, [state.fase]);

  const call = useCallback(
    async (destino: DestinoDeLlamada): Promise<boolean> => {
      if (estaOcupado(estadoRef.current)) {
        notify.warning(OCUPADO);
        return false;
      }
      const cuerpo = objetivo(destino);
      const audio = audioRef.current;
      if (!cuerpo || !audio) {
        notify.error(SIN_INICIAR);
        return false;
      }
      const intento = ++intentoRef.current;
      const vigente = () => intento === intentoRef.current;
      despachar({ tipo: 'pedir', destino });

      let microfono: MediaStream;
      try {
        microfono = await abrirMicrofono();
      } catch (error) {
        if (vigente()) {
          despachar({ tipo: 'abortar' });
          notify.error(motivoDeMicrofono(error));
        }
        return false;
      }
      if (!vigente()) {
        cerrarMicrofono(microfono);
        return false;
      }

      let respuesta;
      try {
        respuesta = await api.softphone.startCall(tenant.id, cuerpo);
      } catch (error) {
        cerrarMicrofono(microfono);
        if (vigente()) {
          despachar({ tipo: 'abortar' });
          notify.error(isApiError(error) ? error.message : SIN_INICIAR);
        }
        return false;
      }
      if (!vigente()) {
        cerrarMicrofono(microfono);
        return false;
      }

      let sesionSip: typeof import('./sesion-sip');
      try {
        sesionSip = await import('./sesion-sip');
      } catch {
        cerrarMicrofono(microfono);
        if (vigente()) {
          despachar({ tipo: 'abortar' });
          notify.error(SIN_INICIAR);
        }
        return false;
      }
      if (!vigente()) {
        cerrarMicrofono(microfono);
        return false;
      }

      despachar({
        tipo: 'credenciales',
        callId: respuesta.callId,
        nombre: respuesta.displayName,
        telefono: respuesta.phone,
      });
      const avisar = (evento: EventoDeLlamada) => {
        if (vigente()) despachar(evento);
      };
      llamadaRef.current = sesionSip.iniciarLlamada({
        sip: respuesta.sip,
        telefono: respuesta.phone,
        microfono,
        audio,
        avisos: {
          alSonar: () => avisar({ tipo: 'progreso' }),
          alContestar: () => avisar({ tipo: 'contestada', en: Date.now() }),
          alTerminar: () => avisar({ tipo: 'terminada', en: Date.now() }),
          alRechazar: ({ fase, motivo }) =>
            avisar(
              fase === 'ended'
                ? { tipo: 'terminada', en: Date.now(), motivo }
                : { tipo: 'fallida', en: Date.now(), motivo },
            ),
          alFallar: (motivo) => avisar({ tipo: 'fallida', en: Date.now(), motivo }),
        },
      });
      return true;
    },
    [tenant.id],
  );

  const hangUp = useCallback(() => {
    if (!estaOcupado(estadoRef.current)) return;
    llamadaRef.current?.colgar();
    llamadaRef.current = null;
    if (estadoRef.current.fase === 'requesting') intentoRef.current += 1;
    despachar({ tipo: 'colgar', en: Date.now() });
  }, []);

  const toggleMute = useCallback(() => {
    const actual = estadoRef.current;
    if (actual.fase !== 'in_call') return;
    llamadaRef.current?.silenciar(!actual.silenciado);
    despachar({ tipo: 'silencio', valor: !actual.silenciado });
  }, []);

  const sendDigit = useCallback((tecla: string) => {
    if (estadoRef.current.fase === 'in_call' && esTecla(tecla)) llamadaRef.current?.marcar(tecla);
  }, []);

  const dismiss = useCallback(() => despachar({ tipo: 'descartar' }), []);

  const reclamarPanel = useCallback(() => {
    setReclamos((n) => n + 1);
    return () => setReclamos((n) => n - 1);
  }, []);

  useEffect(() => {
    const alSalir = () => llamadaRef.current?.colgar();
    const antesDeSalir = (evento: BeforeUnloadEvent) => {
      if (estaOcupado(estadoRef.current)) evento.preventDefault();
    };
    window.addEventListener('pagehide', alSalir);
    window.addEventListener('beforeunload', antesDeSalir);
    return () => {
      window.removeEventListener('pagehide', alSalir);
      window.removeEventListener('beforeunload', antesDeSalir);
      alSalir();
    };
  }, []);

  const durationSeconds = segundosDeConversacion(state, ahora);
  const lastResult = useMemo(() => resultadoDe(state), [state]);

  const value = useMemo<Softphone>(
    () => ({
      enabled,
      configLista,
      state,
      durationSeconds,
      lastResult,
      call,
      hangUp,
      toggleMute,
      sendDigit,
      dismiss,
      panelReclamado: reclamos > 0,
      reclamarPanel,
    }),
    [enabled, configLista, state, durationSeconds, lastResult, call, hangUp, toggleMute, sendDigit, dismiss, reclamos, reclamarPanel],
  );

  return (
    <SoftphoneContext.Provider value={value}>
      {children}
      <audio ref={audioRef} autoPlay hidden aria-hidden />
    </SoftphoneContext.Provider>
  );
}
